import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import {
  createLabAPI,
  inviteUserAPI,
  fetchAllLabsAPI,
  cloneEntryAPI,
  deleteLabAPI,
  updateLabTagsAPI,
} from "views/APIs/APIUtility";

// @material-ui/icons
// import ContentCopy from "@material-ui/icons/ContentCopy";
import {
  Dialog,
  TextField,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
  Box,
} from "@material-ui/core";

// core components

import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import { CloudUpload, Create, DoneAll } from "@material-ui/icons";
import CreateCourseCard from "views/Components/CreateCourseCard";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import CreateLabModal from "views/Components/CreateLabModal";
import LabDetails from "../Models/LabDetails";
import { fetchImageAsFile } from "views/Dashboard/Course/CourseConstants";
import { cloneLabAPI } from "views/APIs/APIUtility";

const useStyles = makeStyles({
  ...styles,
});

export default function LabsDesign() {
  const asset_identifier = "lab";
  const images = require.context("assets/img/create_cards_16_9/", true);

  // state variables
  const { path, url } = useRouteMatch();
  const classes = useStyles();
  const [createLabModal, setCreateLabModal] = React.useState(false);
  const [newLabDetails, setNewLabDetails] = React.useState(new LabDetails());
  const [iconImage, setIconImage] = React.useState();
  const [allLabs, setAllLabs] = React.useState([]);
  const [initialLabs, setInitialLabs] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });

  const [labAdded, setLabAdded] = React.useState(false);
  const { showNotification } = React.useContext(NotificationContext);

  const handleDeleteLab = async (labData) => {
    const formData = new FormData();
    formData.append("lab_id", labData._id);

    try {
      const data = await deleteLabAPI(formData);
      setAllLabs(allLabs.filter((lab) => lab._id !== labData._id));
      showNotification("Lab deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleCloneLab = async (labData) => {
    try {
      setLoading({ loading: true, message: "Cloning Lab..." });
      const formData = new FormData();
      formData.append("lab_id", labData._id);
      const data = await cloneLabAPI(formData);
      setAllLabs((prevLabs) => [...prevLabs, _mapResourceToAppType(data)]);
      setLabAdded(true);
      showNotification("Lab cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const requestData = {
        collection_type: asset_identifier,
        project_id: data._id,
        invitee_email: data.email,
      };

      const response = await inviteUserAPI(requestData);

      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allLabs.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };

      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allCourses state
      const updatedLabs = allLabs.map((lab) =>
        lab._id === updatedProjectId ? newAsset : lab
      );

      setAllLabs(updatedLabs);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch all labs
      try {
        setLoading({ loading: true, message: "Loading Labs..." });
        const data = await fetchAllLabsAPI();
        const mappedData = data.map(_mapResourceToAppType);
        setAllLabs(mappedData);
        setInitialLabs(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchData();
  }, []);

  const handleCreateLab = async () => {
    setLoading({ loading: true, message: "Creating Lab..." });
    const formData = new FormData();
    formData.append("lab_name", newLabDetails.name);
    formData.append("lab_description", newLabDetails.description);
    formData.append("lab_outline", newLabDetails.labOutline);
    newLabDetails.uploadedFiles.forEach((file) => {
      formData.append("files", file);
    });
    formData.append(
      "lab_image",
      iconImage || (await fetchImageAsFile(images("./labs.png")))
    );

    try {
      const data = await createLabAPI(formData);
      window.open(`/create/labdesign/lab/${data._id}`, "_blank");
      setAllLabs([...allLabs, _mapResourceToAppType(data)]);
      setLabAdded(true);
      showNotification("Lab created successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      handleCreateLabModalClose();
      setLoading({ loading: false, message: "" });
    }
  };

  const clearCreateModal = () => {
    setNewLabDetails(new LabDetails());
    setIconImage(null);
  };

  const handleCreateLabModalClose = () => {
    setCreateLabModal(false);
    // setModal(false);
    clearCreateModal();
  };

  const handleFileUpload = (files) => {
    setNewLabDetails({ ...newLabDetails, uploadedFiles: files });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "Ready for Publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      default:
        return <Create style={{ color: "#435966" }} />;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude course_name and course_description
    const { lab_name, lab_description, lab_image, ...rest } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: lab_name, // Add name property
      description: lab_description, // Add description property
      image: lab_image, // Add image property
    };
  };

  const handleSaveLabel = async (labData) => {
    try {
      const formData = new FormData();
      formData.append("lab_id", labData._id);
      if (labData.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        labData.tags.forEach((tag) => formData.append("tags", tag));
      }
      await updateLabTagsAPI(formData);
      setAllLabs((prevLabs) =>
        prevLabs.map((lab) =>
          lab._id === labData._id ? { ...lab, tags: labData.tags } : lab
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateLabModal
        newLabDetails={newLabDetails}
        setNewLabDetails={setNewLabDetails}
        createLabModal={createLabModal}
        handleCreateLabModalClose={handleCreateLabModalClose}
        handleCreateLab={handleCreateLab}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Lab Design Dashboard"
        welcomeSubtitle="Start by creating a new lab or reviewing your existing labs."
        allAssets={allLabs}
        setAllAssets={setAllLabs}
        initialAssets={initialLabs}
        noAssetMessage="Create or Review your labs to get started."
        linkPath="/lab/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateLabModal}
        createAssetButtonText="Create New Lab"
        handleSaveLabel={handleSaveLabel}
        handleInviteSend={handleInviteSend}
        asset_identifier={asset_identifier}
        handleDeleteAsset={handleDeleteLab}
        handleCloneAsset={handleCloneLab}
        assetAdded={labAdded}
        setAssetAdded={setLabAdded}
        setInitialAssets={setInitialLabs}
        workflowConfig={{
          type: "lab",
          title: "Lab Design Workflow",
          description: `The lab design workflow takes you from raw resources to a polished, collaborative lab with a technical notebook and an interactive application.`,
          steps: [
            {
              label: "1. Upload resources & define requirements",
              body: "Start with your source materials and your requirements",
            },
            {
              label: "2. Build & review",
              body:
                "Build the notebook and/or the application, review with collaborators, and refine iteratively.",
            },
            {
              label: "3. Finalize, document & deliver",
              body:
                "Assemble the final deliverables including the notebook, the application, the user guide and the documentation.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/lab-design-demo.mp4",
          examples: [
            {
              title: "UoM Homogeneity & Clustering Explorer",
              description:
                "Simulate the definition and grouping of operational risk Units of Measure (UoMs) using business logic and statistical clustering.",
              link: "https://qulabs.qusandbox.com/lab/68712578cc0de9e54985d44a",
              mediaUrl:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/lab-design/lab-design-ild-example.mp4",
              mediaAlt: "Lab Design Demo",
            },
            {
              title: "Risk Appetite & Allocation Modeler",
              description:
                "Simulates how a firm's Board-level risk appetite cascades down to business units and how actual exposures roll back up to form the total firm risk profile. ",
              link: "https://qulabs.qusandbox.com/lab/687178243021171aaf6d2577",
              mediaUrl:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/lab-design/lab-design-risk-appetite-example.mp4",
              mediaAlt: "Lab Design Demo",
            },
            {
              title: "Profitabilty & Risk Pricing Lab",
              description:
                "Explores different stress testing methodologies—Sensitivity, Scenario, and Firm-Wide—to assess the financial impact of correlated market threats.",
              link:
                "https://qulabs.qusandbox.com/lab/687153b85887adf827c47db5/",
              mediaUrl:
                "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/lab-design/lab-design-risk-stress-test-example.mp4",
              mediaAlt: "Lab Design Demo",
            },
          ],
        }}
      />
    </div>
  );
}
